import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Image,
  Server,
  CheckCircle,
  RefreshCw,
  Clock,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { imageLogs, imageBackends, ImageStats, ImageBackend } from '../../api/client';
import { formatNumber, cn } from '../../lib/utils';
import WanVideoModelControl from '../../components/WanVideoModelControl';

type ImageStatsResponse = ImageStats & {
  totalRequests?: number;
  successfulRequests?: number;
  avgResponseTime?: number;
};

export default function ImageDashboard() {
  const [stats, setStats] = useState<ImageStatsResponse | null>(null);
  const [backends, setBackends] = useState<ImageBackend[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [statsRes, backendsRes] = await Promise.all([
        imageLogs.stats(),
        imageBackends.list(),
      ]);

      setStats(statsRes.stats as ImageStatsResponse);
      setBackends(backendsRes.backends);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const totalRequests = stats?.totalRequests ?? 0;
  const successfulRequests = stats?.successfulRequests ?? 0;
  const avgResponseTime = stats?.avgResponseTime ?? 0;
  const successRate = totalRequests > 0
    ? ((successfulRequests / totalRequests) * 100).toFixed(1)
    : '0';

  const activeBackends = backends.filter(b => b.isActive).length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient-violet">Image Dashboard</h1>
          <p className="text-muted-foreground mt-1">ComfyUI generation analytics</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      <WanVideoModelControl />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Generations (24h)"
          value={formatNumber(totalRequests)}
          trend={stats?.requestsTrend || 0}
          icon={Image}
          colorClass="stat-card-violet"
          iconColorClass="text-violet-400"
          delay={100}
        />
        <StatsCard
          title="Success Rate"
          value={`${successRate}%`}
          icon={CheckCircle}
          colorClass="stat-card-emerald"
          iconColorClass="text-emerald-400"
          delay={200}
        />
        <StatsCard
          title="Avg Response"
          value={`${(avgResponseTime / 1000).toFixed(1)}s`}
          icon={Clock}
          colorClass="stat-card-amber"
          iconColorClass="text-amber-400"
          delay={300}
        />
        <StatsCard
          title="Active Backends"
          value={`${activeBackends}/${backends.length}`}
          icon={Server}
          colorClass="stat-card-cyan"
          iconColorClass="text-cyan-400"
          delay={400}
        />
      </div>

      {/* Backend Status Grid */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-500">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold">Backend Status</h2>
            <p className="text-sm text-muted-foreground">Real-time health monitoring</p>
          </div>
        </div>

        {backends.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Server className="w-12 h-12 mb-4 opacity-50" />
            <p>No backends configured</p>
            <p className="text-sm mt-1">Add a backend to start generating images</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {backends.map((backend, index) => (
              <BackendCard key={backend.id} backend={backend} delay={index * 50} />
            ))}
          </div>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backend Performance */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-600">
          <div className="mb-6">
            <h2 className="text-lg font-semibold">Backend Performance</h2>
            <p className="text-sm text-muted-foreground">Requests by backend (24h)</p>
          </div>
          <div className="h-64">
            {backends.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={backends.map(b => ({
                    name: b.name,
                    successful: b.stats.successfulRequests,
                    failed: b.stats.failedRequests,
                  }))}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" horizontal={false} />
                  <XAxis type="number" stroke="hsl(215 20% 45%)" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke="hsl(215 20% 45%)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(222 47% 10%)',
                      border: '1px solid hsl(222 30% 20%)',
                      borderRadius: '12px',
                    }}
                  />
                  <Bar dataKey="successful" fill="#10b981" stackId="a" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="failed" fill="#f43f5e" stackId="a" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Response Time Distribution */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-700">
          <div className="mb-6">
            <h2 className="text-lg font-semibold">Avg Response Time</h2>
            <p className="text-sm text-muted-foreground">By backend (ms)</p>
          </div>
          <div className="h-64">
            {backends.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={backends.map(b => ({
                    name: b.name,
                    avgTime: Math.round(b.stats.avgResponseTime),
                  }))}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" horizontal={false} />
                  <XAxis
                    type="number"
                    stroke="hsl(215 20% 45%)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${(v / 1000).toFixed(1)}s`}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke="hsl(215 20% 45%)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(222 47% 10%)',
                      border: '1px solid hsl(222 30% 20%)',
                      borderRadius: '12px',
                    }}
                    formatter={(value: number) => [`${(value / 1000).toFixed(2)}s`, 'Avg Time']}
                  />
                  <Bar dataKey="avgTime" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsCard({
  title,
  value,
  trend,
  icon: Icon,
  colorClass,
  iconColorClass,
  delay,
}: {
  title: string;
  value: string;
  trend?: number;
  icon: typeof Activity;
  colorClass: string;
  iconColorClass: string;
  delay: number;
}) {
  return (
    <div
      className={cn('card-glow rounded-2xl p-6 animate-fade-in-up', colorClass)}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center bg-secondary', iconColorClass)}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div
            className={cn(
              'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg',
              trend > 0
                ? 'bg-emerald-500/15 text-emerald-400'
                : trend < 0
                ? 'bg-rose-500/15 text-rose-400'
                : 'bg-secondary text-muted-foreground'
            )}
          >
            {trend > 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : trend < 0 ? (
              <TrendingDown className="w-3 h-3" />
            ) : (
              <Minus className="w-3 h-3" />
            )}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
        <p className="text-3xl font-bold tracking-tight">{value}</p>
      </div>
    </div>
  );
}

function BackendCard({ backend, delay }: { backend: ImageBackend; delay: number }) {
  const statusStyles: Record<string, { text: string; badge: string }> = {
    emerald: { text: 'text-emerald-400', badge: 'bg-emerald-500/15 text-emerald-400' },
    rose: { text: 'text-rose-400', badge: 'bg-rose-500/15 text-rose-400' },
  };
  const statusKey = backend.isActive ? 'emerald' : 'rose';
  const statusText = backend.isActive ? 'Active' : 'Inactive';
  const styles = statusStyles[statusKey];

  return (
    <div
      className="card-glow rounded-xl p-4 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Server className={cn('w-4 h-4', styles.text)} />
          <span className="font-medium truncate max-w-[120px]" title={backend.name}>
            {backend.name}
          </span>
        </div>
        <div
          className={cn(
            'px-2 py-1 text-xs font-medium rounded-lg',
            styles.badge
          )}
        >
          {statusText}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-muted-foreground text-xs">Type</p>
          <p className="font-mono text-xs capitalize">{backend.type}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Priority</p>
          <p className="font-mono text-xs">{backend.priority}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Success</p>
          <p className="font-mono text-xs text-emerald-400">{backend.stats.successfulRequests}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Failed</p>
          <p className="font-mono text-xs text-rose-400">{backend.stats.failedRequests}</p>
        </div>
      </div>

    </div>
  );
}
